import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SOSInput = () => {
  const [sosData, setSosData] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [sosList, setSosList] = useState([]);

  useEffect(() => {
    loadSOSList();
  }, []);

  const loadSOSList = async () => {
    try {
      console.log('🔄 Loading SOS list...');
      const response = await axios.get('/api/sos/list');
      console.log('📥 SOS List Response:', response.data);
      
      if (response.data?.data?.sosIncs && Array.isArray(response.data.data.sosIncs)) {
        console.log('✅ Setting SOS list:', response.data.data.sosIncs.length, 'entries');
        setSosList(response.data.data.sosIncs);
      } else {
        console.log('⚠️ No SOS data found in response');
        setSosList([]);
      }
    } catch (error) {
      console.error('❌ Fehler beim Laden der SOS-Liste:', error);
      setSosList([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sosData.trim()) {
      setMessage('Bitte SOS-Daten eingeben');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await axios.post('/api/sos/submit', { sosData });
      setMessage(response.data?.message || 'SOS-Daten erfolgreich gespeichert');
      setMessageType('success');
      setSosData('');
      loadSOSList(); // Aktualisiere die Liste
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Speichern der SOS-Daten';
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSOS = async (sosId) => {
    try {
      const response = await axios.delete(`/api/sos/${sosId}`);
      setMessage(response.data?.message || 'SOS gelöscht');
      setMessageType('success');
      loadSOSList();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Löschen der SOS';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('de-DE');
    } catch {
      return 'Unbekannt';
    }
  };

  const formatCoords = (coords) => {
    if (coords && typeof coords === 'object' && coords.x && coords.y) {
      return `${coords.x}|${coords.y}`;
    }
    return 'Unbekannt';
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 rounded-full shadow-lg mb-4">
            <span className="text-2xl text-white">🚨</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            SOS-Eingabe
          </h1>
          <p className="text-xl text-gray-600">Notfall-Meldungen für schnelle Verteidigung</p>
        </div>

        {/* Status Message */}
        {message && (
          <div className={`px-6 py-4 rounded-lg border ${
            messageType === 'success' ? 'bg-green-50 border-green-200 text-green-700' :
            messageType === 'error' ? 'bg-red-50 border-red-200 text-red-700' :
            'bg-blue-50 border-blue-200 text-blue-700'
          }`}>
            <div className="font-medium">{message}</div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* SOS Input Form */}
          <div className="card">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              🚨 SOS-Daten eingeben
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="form-label">
                  SOS-Daten (BB-Codes)
                </label>
                <textarea
                  value={sosData}
                  onChange={(e) => setSosData(e.target.value)}
                  className="form-input h-64 font-mono text-sm"
                  placeholder={`Beispiel BB-Code Format:
[coord]450|546[/coord] wird angegriffen von:
[coord]445|550[/coord] um 12:34:56
[coord]448|542[/coord] um 12:45:30
...`}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-danger w-full"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="spinner mr-2"></div>
                    Verarbeite SOS-Daten...
                  </span>
                ) : (
                  'SOS-Daten verarbeiten'
                )}
              </button>
            </form>
          </div>

          {/* SOS Statistics */}
          <div className="card">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              📊 SOS-Statistiken
            </h2>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-red-50 text-red-800 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold">{sosList.length}</div>
                <div className="text-sm">SOS-Meldungen</div>
              </div>
              
              <div className="bg-orange-50 text-orange-800 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold">
                  {sosList.reduce((total, sos) => total + (sos.incs?.length || 0), 0)}
                </div>
                <div className="text-sm">Angriffe</div>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h3 className="text-lg font-bold text-yellow-800 mb-3">📋 Eingabe-Hilfe</h3>
              <div className="text-yellow-700 text-sm space-y-2">
                <div>• BB-Codes aus SOS-Nachrichten kopieren</div>
                <div>• Koordinaten und Zeiten werden automatisch erkannt</div>
                <div>• Mehrere SOS gleichzeitig möglich</div>
                <div>• Schnelle Verarbeitung für Tab-Berechnung</div>
              </div>
            </div>
          </div>
        </div>

        {/* Active SOS List */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              🚨 Aktive SOS-Meldungen ({sosList.length})
            </h2>
          </div>

          {sosList.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl text-gray-400">🚨</span>
              </div>
              <p className="text-gray-500 text-lg mb-2">Keine SOS-Meldungen</p>
              <p className="text-gray-400">Geben Sie SOS-Daten ein für schnelle Verteidigung</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {sosList.map((sos) => (
                <div key={sos._id} className="bg-red-50 rounded-lg border border-red-200 p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                      <h3 className="text-lg font-bold text-red-800">
                        SOS #{sos._id.slice(-6)}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleDeleteSOS(sos._id)}
                      className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-100 transition-colors"
                      title="SOS löschen"
                    >
                      🗑️
                    </button>
                  </div>

                  <div className="space-y-3 text-red-800">
                    <div className="flex justify-between">
                      <span className="font-medium">Ziel:</span>
                      <span className="coords">{formatCoords(sos.targetCoords)}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="font-medium">Angriffe:</span>
                      <span className="font-bold">{sos.incs?.length || 0}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="font-medium">Erstellt:</span>
                      <span className="text-sm">{formatDate(sos.createdAt)}</span>
                    </div>
                  </div>

                  {sos.incs && sos.incs.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-red-200">
                      <div className="text-red-700 text-sm font-medium mb-2">Nächster Angriff:</div>
                      <div className="bg-red-100 rounded p-3">
                        <div className="text-red-800 text-sm">
                          Von: <span className="coords">{formatCoords(sos.incs[0].originCoords)}</span>
                        </div>
                        <div className="text-red-700 text-xs mt-1">
                          {formatDate(sos.incs[0].arrivalTime)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SOSInput;
